import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hero } from '../components/Hero';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { RevealWrapper } from '../hooks/useReveal';
import { Shield, Zap, Cloud, Lock, Server, Gauge, Sparkles, Rocket, Users } from 'lucide-react';

const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

export const HomePage = () => {
  const [basePrice, setBasePrice] = useState(null);

  useEffect(() => {
    fetch(`${backendUrl}/api/config/pricing`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setBasePrice(data.base_price); })
      .catch(() => {});
  }, []);

  const priceDisplay = basePrice ? basePrice.toFixed(2).replace('.', ',') : '9,00';

  // SEO: Update document title and meta description for homepage
  useEffect(() => {
    document.title = 'Managed Nextcloud Hosting | Hochverfügbar & selbstheilend | Wolkenhelden';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', `Professionelles Managed Nextcloud Hosting mit S3-Storage. Hochverfügbar, DSGVO-konform, Made in Germany. Ab ${priceDisplay}€/Monat.`);
    }
  }, [priceDisplay]);

  const features = [
    {
      icon: Zap,
      title: 'Blitzschnell ⚡',
      description: 'Der HA-Cluster skaliert, S3 liefert – deine Daten sind da, wenn du sie brauchst. Wie ein echter Superheld.'
    },
    {
      icon: Shield,
      title: 'Helden-Schutz 🛡️',
      description: 'Security-Best Practices, saubere Isolation, regelmäßige Updates – ohne dass du nachts wach liegst.'
    },
    {
      icon: Server,
      title: 'Selbstheilend 💪',
      description: 'Wenn ein Knoten ausfällt, übernimmt ein anderer. Deine Wolke bleibt am Start wie ein wahrer Held.'
    },
    {
      icon: Lock,
      title: 'Rundum-Sorglos 🦸',
      description: 'Wir kümmern uns um Betrieb, Updates und Stabilität. Du konzentrierst dich auf dein Team.'
    },
    {
      icon: Cloud,
      title: 'Helden-Speicher ☁️',
      description: 'S3-Objektspeicher skaliert sauber mit deinen Daten. Schnell & robust als primärer Storage.'
    },
    {
      icon: Gauge,
      title: 'Turbo-Power 🚀',
      description: 'Hochverfügbarkeit mit Rolling Updates und Self-Healing. Orchestrierung statt Bastellösung.'
    },
  ];

  return (
    <div>
      <Hero />

      {/* Why Wolkenhelden Section */}
      <section className="py-16 md:py-24" data-testid="why-section">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <RevealWrapper>
            <div className="mb-12 text-center">
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                Warum du ein <span className="text-primary">Wolkenheld</span> werden solltest
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Managed. Sicher. Schnell. Heldenhaft. 🦸‍♂️
              </p>
            </div>
          </RevealWrapper>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, i) => (
              <RevealWrapper key={i} delay={i * 100}>
                <Card
                  className="glass-card h-full transition-all hover:shadow-lg hover:-translate-y-1 hover:border-primary/30"
                  data-testid={`feature-card-${i}`}
                >
                  <CardHeader>
                    <feature.icon className="h-8 w-8 text-primary mb-2" />
                    <CardTitle className="font-display text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">{feature.description}</CardDescription>
                  </CardContent>
                </Card>
              </RevealWrapper>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-muted/30" data-testid="how-it-works-section">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <RevealWrapper>
            <div className="mb-12 text-center">
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                In 3 Schritten zum <span className="text-primary">Cloud-Helden</span>
              </h2>
            </div>
          </RevealWrapper>

          <div className="grid gap-8 md:grid-cols-3">
            {[
              {
                step: '1',
                icon: '🦸',
                title: 'Superkräfte wählen',
                description: 'Speicher, Team-Power, Schutzschild, Turbo-Antrieb – stelle deinen Helden-Tarif zusammen.'
              },
              {
                step: '2',
                icon: '⚡',
                title: 'Mission starten',
                description: 'In 2–3 Minuten ist deine Bestellung durch – schneller als jeder Superheld fliegen kann.'
              },
              {
                step: '3',
                icon: '🚀',
                title: 'Abheben!',
                description: 'Wir provisionieren automatisch. Du bekommst deine URL und bist startklar.'
              },
            ].map((item, i) => (
              <RevealWrapper key={i} delay={i * 100}>
                <div className="text-center" data-testid={`step-${i}`}>
                  <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-primary to-blue-600 text-4xl font-bold text-primary-foreground shadow-lg shadow-primary/30">
                    {item.icon}
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </RevealWrapper>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-24" data-testid="stats-section">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 md:grid-cols-4">
            {[
              { value: '99.9%', label: 'Helden-Uptime', icon: '⚡' },
              { value: '< 1s', label: 'Reaktionszeit', icon: '🚀' },
              { value: '24/7', label: 'Helden-Support', icon: '🦸' },
              { value: '100%', label: 'Made in Germany', icon: '🇩🇪' },
            ].map((stat, i) => (
              <RevealWrapper key={i} delay={i * 100}>
                <div className="text-center p-6 rounded-lg bg-gradient-to-br from-primary/5 to-transparent">
                  <div className="text-3xl mb-2">{stat.icon}</div>
                  <div className="text-4xl font-bold text-primary font-display">{stat.value}</div>
                  <div className="text-muted-foreground mt-1">{stat.label}</div>
                </div>
              </RevealWrapper>
            ))}
          </div>
        </div>
      </section>

      {/* DSGVO Section */}
      <section className="py-16 md:py-24 bg-muted/30" data-testid="dsgvo-section">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <RevealWrapper>
            <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
              {/* Image */}
              <div className="flex-shrink-0">
                <img 
                  src="/images/dsgvo_held.png"
                  alt="Wolkenheld mit DSGVO Ready Schild"
                  className="w-64 h-64 md:w-80 md:h-80 object-contain drop-shadow-2xl"
                  data-testid="dsgvo-hero-image"
                />
              </div>
              {/* Content */}
              <div className="text-center lg:text-left">
                <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl mb-4">
                  <span className="text-primary">DSGVO-konform</span> & sicher
                </h2>
                <p className="text-lg text-muted-foreground mb-6 max-w-xl">
                  Deine Daten sind bei uns in guten Händen. Wir hosten ausschließlich in deutschen Rechenzentren 
                  und erfüllen alle Anforderungen der DSGVO. So kannst du dich auf das Wesentliche konzentrieren – 
                  wir kümmern uns um den Datenschutz.
                </p>
                <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Shield className="h-5 w-5 text-primary" />
                    <span>Deutsche Rechenzentren</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Lock className="h-5 w-5 text-primary" />
                    <span>Verschlüsselte Übertragung</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-5 w-5 text-primary" />
                    <span>AVV auf Anfrage</span>
                  </div>
                </div>
              </div>
            </div>
          </RevealWrapper>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary/10 via-transparent to-blue-600/10" data-testid="cta-section">
        <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <RevealWrapper>
            <Card className="glass-card border-primary/30 text-center overflow-hidden relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-blue-500 to-primary" />
              <CardHeader className="pt-8">
                <div className="flex justify-center mb-4">
                  <div className="p-4 rounded-full bg-gradient-to-br from-primary to-blue-600 shadow-lg shadow-primary/30">
                    <Sparkles className="h-8 w-8 text-white" />
                  </div>
                </div>
                <CardTitle className="font-display text-3xl sm:text-4xl">
                  Bereit, ein <span className="text-primary">Cloud-Held</span> zu werden?
                </CardTitle>
                <CardDescription className="text-lg mt-4">
                  Starte ab heldenhaften <span className="font-bold text-primary">{priceDisplay} €</span> pro Monat.
                  <br />Wir kümmern uns um den Rest – versprochen! 🤝
                </CardDescription>
              </CardHeader>
              <CardContent className="pb-8">
                <Button asChild size="lg" className="bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90 shadow-lg shadow-primary/25 text-lg px-8">
                  <Link to="/preise" className="flex items-center gap-2">
                    <Zap className="h-5 w-5" />
                    Werde jetzt zum Helden!
                  </Link>
                </Button>
                <p className="mt-4 text-sm text-muted-foreground">
                  ✨ Keine Knebelverträge • Monatlich kündbar • Made in Germany 🇩🇪
                </p>
              </CardContent>
            </Card>
          </RevealWrapper>
        </div>
      </section>
    </div>
  );
};
